/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";

import { uniq, flatten, values } from "lodash";
import { validators, createValidator } from "not-valid";
import { Container, Loader, Header, Button, Segment, Divider, Label, Grid } from "semantic-ui-react";
import * as moment from "moment";

import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";
import { FileQuestion } from "@teamprevent/common/questionnaires/components/questions/FileQuestion";
import { QUESTIONNAIRE_ID, CUSTOMER_ID } from "@teamprevent/common/questionnaires/routerParams";
import { HrRepresentative } from "@teamprevent/common/hrRepresentatives/models";
import {
    CustomQuestionConfig,
    QuestionnaireConfig,
    AdditionalQuestionsSectionId,
    FurtherQuestionsSectionTitle,
    renderAdditionalQuestions,
    isQuestionMandatory,
    isQuestionDisabled
} from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import * as questionIds from "@teamprevent/common/questionnaires/questionnaireConfig/referral/questionIds";
import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { dateOfBirthInRange } from "@teamprevent/common/validators/dateOfBirthInRange";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

/* eslint-disable no-restricted-imports */
// This is doing no real harm but app.common should not import admin portal specific code
// We should change the in the future so that the admin portal injects this via a prop instead.
import { DeleteReferralFormDocumentModal } from "../../../../../Admin.App/src/crud/ohCase/components/details/DeleteReferralFormDocumentModal";
import { Authorize, ADMIN_ROLES } from "../../../../../Admin.App/src/auth";
/* eslint-enable no-restricted-imports */

import { updateQuestion, removeQuestion, clearQuestionnaire, resetActiveSections } from "../../actions";
import { AnsweredQuestion, AnsweredQuestionWithId, FileAnswer, QuestionnairesState, OrgLevel, QuestionAnswer, QuestionnaireTypes, Section } from "../../model";
import { RadioButtonQuestion, RadioButton } from "../questions/RadioButtonQuestion";
import { DropdownQuestion } from "../questions/DropdownQuestion";
import { DateQuestion } from "../questions/DateQuestion";
import { ContextAwareProgressBarQuestionnaireSection } from "../ContextAwareProgressBarQuestionnaireSection";
import { CheckboxQuestion } from "../questions/CheckboxQuestion";
import { TextQuestion } from "../questions/TextQuestion";
import { isCustomerUser } from "../../../auth/roles";
import { LineManager } from "../../../lineManagers/models";
import { GENDER_OPTIONS } from "../../../utils";
import { toast } from "../../../toasts";
import { purchaseOrderNumberValidator } from "../../../ohCase/validators/purchaseOrderNumberValidator";
import { ContextAwareProgressBarQuestionnaireContainer } from "../ContextAwareProgressBarQuestionnaireContainer";

import { DATE_FORMAT, LoadReferralRequestModel, ReferralFormState } from "./models";
import { loadReferralQuestionnaire, saveReferralQuestionnaire, submitReferralQuestionnaire } from "./actions";

import { Location } from "redux-little-router";

import "../../styles/referralForm/referral-form.scss";

export const REFERRAL_QUESTIONNAIRE_KEY = "referralForm";

export interface ReferralFormStateProps {
    currentUser: { name: string; id: string; email: string; mobilePhone: string; roles: string[] };
    lineManagers: LineManager[];
    hrRepresentatives: HrRepresentative[];
    questions: AnsweredQuestionWithId[];
    eTag: string | null | undefined;
    referralSubmittedDate?: moment.Moment;
    orgLevels: OrgLevel[];
    ohCaseId?: string;
    customerId?: string;
    questionnaireId?: string;
    sections: Section[];
}

interface BaseReferralFormOwnProps {
    readonly: boolean;
    questionnaireConfig: QuestionnaireConfig;
    api?: string;
    loadOrgStructure?: (parentOrgStructureLevelId: string) => void;
    useAutoSave?: boolean;
    referAgainOhCaseId?: string;
    documentBaseApi: string;
}

interface SubmittableReferralFormOwnProps extends BaseReferralFormOwnProps {
    readonly: false;
    afterSubmit: (ohCaseId: string) => void;
}

interface ReadonlyReferralFormOwnProps extends BaseReferralFormOwnProps {
    readonly: true;
}

export type ReferralFormOwnProps = SubmittableReferralFormOwnProps | ReadonlyReferralFormOwnProps;

export interface ReferralFormOwnState {
    selectedOrgStructureLevelIds: string[];
    documentCount: number;
    initialOrgStructuresLoaded: boolean;
    submitInProgress: boolean;
}

export interface ReferralFormDispatchProps {
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => void;
    removeQuestion: (payload: { sectionId: string; id: string }) => void;
    clearQuestionnaire: () => void;
    resetActiveSections: () => void;
    loadQuestionnaire: (requestModel: LoadReferralRequestModel, abortController?: AbortController) => void;
    submitQuestionnaire: (apiUrl: string, questions: AnsweredQuestionWithId[], referAgainOhCaseId: string | undefined) => Promise<string>;
    saveReferral: (
        questions: AnsweredQuestionWithId[],
        eTag: string | null | undefined,
        isInitial: boolean,
        referAgainOhCaseId: string | undefined
    ) => Promise<void>;
}

export type ReferralFormProps = ReferralFormStateProps & ReferralFormOwnProps & ReferralFormDispatchProps;

export const lineManagerHrSectionId = "LineManagerHR";
export const employeeDetailSectionId = "ReferredDetails";
export const furtherDetailsSectionId = "FurtherDetails";
const confidentialitySectionId = "confidentiality";

export const selfReferralQuestion = { id: questionIds.SelfReferral, questionText: "Is this a Self Referral?" };
export const selfReferralYes = "Yes";
export const selfReferralNo = "No";

const totallyConfidentialAnswer = "Totally confidential";

const dateOfBirthValidators = [dateOfBirthInRange];
class ContextAwareReferralFormUnconnected extends React.Component<ReferralFormProps, ReferralFormOwnState> {
    private readonly orgLevelQuestionId = "OrgLevel";
    private readonly absenceDetailsSectionId = "AbsenceDetails";
    private readonly absentQuestion = { id: questionIds.Absent, absentAnswer: "Yes" };
    private readonly restrictedDutiesQuestion = { id: questionIds.RestrictedDuties, restrictedDutiesAnswer: "Yes" };

    private readonly jobRoleDetailsSectionId = "JobRoleDetails";
    private readonly prolongedStandingDetailsQuestion = { id: "ProlongedStandingDetails", questionText: "Details" };
    private readonly prolongedSittingDetailsQuestion = { id: "ProlongedSittingDetails", questionText: "Details" };
    private readonly drivingDetailsQuestion = { id: "DrivingDetails", questionText: "Details" };
    private readonly walkingDetailsQuestion = { id: "WalkingDetails", questionText: "Details" };
    private readonly climbingDetailsQuestion = { id: "ClimbingDetails", questionText: "Details" };
    private readonly shiftWorkDetailsQuestion = { id: "ShiftWorkDetails", questionText: "Details" };
    private readonly heavyLiftingDetailsQuestion = { id: "HeavyLiftingDetails", questionText: "Details" };
    private readonly twistingDetailsQuestion = { id: "TwistingDetails", questionText: "Details" };
    private readonly pullingDetailsQuestion = { id: "PullingDetails", questionText: "Details" };
    private readonly pushingDetailsQuestion = { id: "PushingDetails", questionText: "Details" };
    private readonly dseDetailsQuestion = { id: "DseDetails", questionText: "Details" };
    private readonly confinedSpaceDetailsQuestion = { id: "ConfinedSpaceDetails", questionText: "Details" };
    private readonly chemicalsDetailsQuestion = { id: "ChemicalsDetails", questionText: "Details" };
    private readonly biologicalAgentsDetailsQuestion = { id: "BiologicalAgentsDetails", questionText: "Details" };
    private readonly machineryDetailsQuestion = { id: "MachineryDetails", questionText: "Details" };
    private readonly dustOrFumesDetailsQuestion = { id: "DustOrFumesDetails", questionText: "Details" };
    private readonly noiseHazardsDetailsQuestion = { id: "NoiseHazardsDetails", questionText: "Details" };
    private readonly outsideWorkDetailsQuestion = { id: "OutsideWorkDetails", questionText: "Details" };
    private readonly overseasTravelDetailsQuestion = { id: "OverseasTravelDetails", questionText: "Details" };
    private readonly workplaceStressDetailsQuestion = { id: "WorkplaceStressDetails", questionText: "Details" };

    private readonly documentsSectionId = "Documents";
    private canSubmitFormWhenOrgLevelsAreDisabled = false;

    constructor(props: ReferralFormProps) {
        super(props);
        this.loadQuestionnaire = this.loadQuestionnaire.bind(this);
        this.submitQuestionnaire = this.submitQuestionnaire.bind(this);
        this.getQuestionConfig = this.getQuestionConfig.bind(this);
        this.addDocument = this.addDocument.bind(this);
        this.clearQuestionnaire = this.clearQuestionnaire.bind(this);
        this.autoSaveQuestionnaire = this.autoSaveQuestionnaire.bind(this);
        this.state = {
            selectedOrgStructureLevelIds: [],
            documentCount: 0,
            initialOrgStructuresLoaded: false,
            submitInProgress: false,
        };
    }

    public async loadQuestionnaire(abortController?: AbortController) {
        const isWriteModeFromReferAgain = !this.props.readonly && this.props.referAgainOhCaseId && this.props.useAutoSave;
        // Some usages of this component rely on the auto save functionality to actually load the questionnaire
        // so we need to force a call to Save as part of the load when auto save is enabled
        if (this.props.useAutoSave) {
            await this.props.saveReferral(this.props.questions, this.props.eTag, true, this.props.referAgainOhCaseId);
        }
        else if (isWriteModeFromReferAgain) {
            // loading not required as the form data will be returned with the initial "autoSave" call
            return;
        } else if (this.props.customerId && this.props.questionnaireId || this.props.ohCaseId) {
            const requestModel: LoadReferralRequestModel = {
                apiUrl: this.props.api || "/api/referral",
                customerId: this.props.customerId,
                questionnaireId: this.props.questionnaireId,
                ohCaseId: this.props.ohCaseId,
            };
            await this.props.loadQuestionnaire(requestModel, abortController);
        }
    }

    public async submitQuestionnaire(questions: AnsweredQuestionWithId[]) {
        if (this.props.readonly === true) {
            return false;
        }

        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.ReferredTitle, questions);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.ReferredFirstName, questions);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.ReferredLastName, questions);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.PersonalEmailAddress, questions);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.ConfirmPersonalEmailAddress, questions);
        this.trimAnswerAndUpdateEmployeeQuestion(questionIds.JobTitle, questions);

        const mobilePhone = this.getAnswerToQuestionWithId(questionIds.MobilePhone);
        const homePhone = this.getAnswerToQuestionWithId(questionIds.HomePhone);
        const workPhone = this.getAnswerToQuestionWithId(questionIds.WorkPhone);

        if (!mobilePhone && !homePhone && !workPhone) {
            toast.warning("Please provide one form of telephone number");
            return false;
        }

        const invalidQuestions = this.props.questions.filter(question => !question.isValid);

        const anyAdditionalQuestionsTicked = this.anyAnswerTicked([
            questionIds.WorkRelated,
            questionIds.ReferToSpecialist,
            questionIds.ImpairedPerformance,
            questionIds.ReliableInFuture,
            questionIds.FitForDisciplinary,
            questionIds.FitToContinue,
            questionIds.EqualityAct,
            questionIds.AdjustmentsRequired,
            questionIds.RestrictionsApply,
            questionIds.ReturnDate
        ]);

        const additionalInfoQuestion = !!this.getAnswerToQuestionWithId(questionIds.AdditionalInfo);

        if (invalidQuestions?.length === 0 && (anyAdditionalQuestionsTicked || additionalInfoQuestion)) {
            const separateAdditionalQuestionAnswersRequiredQuestionConfig =
                this.getQuestionConfig(questionIds.SeparateAdditionalQuestionAnswersRequired) as CustomQuestionConfig;

            const separateAdditionalQuestionAnswersQuestion = {
                id: questionIds.SeparateAdditionalQuestionAnswersRequired,
                sectionId: "InformationRequired",
                question: separateAdditionalQuestionAnswersRequiredQuestionConfig.questionText,
                answer: separateAdditionalQuestionAnswersRequiredQuestionConfig.questionText,
                isValid: true
            };

            questions.push(separateAdditionalQuestionAnswersQuestion);

            this.props.updateAnswer({
                sectionId: "InformationRequired",
                id: questionIds.SeparateAdditionalQuestionAnswersRequired,
                question: separateAdditionalQuestionAnswersQuestion,
            });
        }

        const ohCaseId = await this.props.submitQuestionnaire(this.props.api || "/api/referral", questions, this.props.referAgainOhCaseId);
        if (invalidQuestions?.length === 0) {
            this.props.afterSubmit(ohCaseId);
        } else {
            const errorMessage = "The following questions are invalid: " + invalidQuestions.map(q => "(" + q.question + ")").join(", ");
            toast.warning(errorMessage);
        }

        return true;
    }

    public componentDidMount() {
        this.canSubmitFormWhenOrgLevelsAreDisabled = getFeatureToggles()[FeatureToggleNames.CanSubmitFormWhenOrgLevelsAreDisabled];
    }

    public componentDidUpdate = (oldProps: ReferralFormProps) => {
        if (this.props.readonly) {
            return;
        }

        if (!this.state.initialOrgStructuresLoaded) {
            this.setInitialOrgStructures();
            return;
        }

        this.updateOrgLevels(oldProps);

        const oldSelfReferralAnswer = oldProps.questions.find(question => question.id === questionIds.SelfReferral)?.answer;
        if (this.hasSelfReferralAnswerChanged(oldSelfReferralAnswer)) {
            this.changeReferralTotallyConfidentialAnswer(this.isSelfReferral());
        }

        const oldOrgLevel1NameAnswer = oldProps.questions.find(question => question.id === questionIds.OrgLevel1Name)?.answer;
        const orgLevel1NameQuestion = this.props.questions.find(question => question.id === questionIds.OrgLevel1Name);
        if (orgLevel1NameQuestion &&
            ((!orgLevel1NameQuestion.answer && orgLevel1NameQuestion.isValid) ||
                this.hasOrgLevel1AnswerChanged(oldOrgLevel1NameAnswer))) {
            this.updateOrgLevel1Validity(orgLevel1NameQuestion);
        }
    };

    private trimAnswerAndUpdateEmployeeQuestion(questionId: string, questions: AnsweredQuestionWithId[]) {

        const stateQuestion = this.props.questions.find(q => q.id === questionId);

        if (stateQuestion) {
            this.props.updateAnswer({
                sectionId: employeeDetailSectionId,
                id: stateQuestion.id,
                question: { ...stateQuestion, answer: stateQuestion.answer?.toString()?.trim() }
            });
        }

        const question = questions.find(q => q.id === questionId);

        if (question) {
            question.answer = question.answer?.toString()?.trim();
        }
    }

    private updateOrgLevel1Validity(orgLevel1NameQuestion: AnsweredQuestionWithId) {

        if (!orgLevel1NameQuestion.answer) {
            this.props.updateAnswer({
                sectionId: furtherDetailsSectionId,
                id: orgLevel1NameQuestion.id,
                question: { ...orgLevel1NameQuestion, isValid: false }
            });
        }
    }

    private async autoSaveQuestionnaire(questions: AnsweredQuestionWithId[], eTag: string | null) {
        if (this.props.useAutoSave) {
            await this.props.saveReferral(questions, eTag, false, this.props.referAgainOhCaseId);
        }
    }

    private clearQuestionnaire() {
        try {
            if (this.props.readonly === false) {
                this.props.clearQuestionnaire();
            }
        } catch (ex: any) {
            if (ex.status === 400) {
                this.loadQuestionnaire(new AbortController());
            } else {
                throw ex;
            }
        }
        finally {
            this.props.resetActiveSections();
        }
    }

    private setInitialOrgStructures() {
        const orgLevelOneIdAnswer = this.getAnswerToQuestionWithId(questionIds.OrgLevel1Id);
        const orgLevelTwoIdAnswer = this.getAnswerToQuestionWithId(questionIds.OrgLevel2Id);
        const orgLevelThreeIdAnswer = this.getAnswerToQuestionWithId(questionIds.OrgLevel3Id);
        const orgLevelFourIdAnswer = this.getAnswerToQuestionWithId(questionIds.OrgLevel4Id);
        let selectedOrgIds: string[] = [];

        if (!this.props.loadOrgStructure)
        {
            return;
        }

        if (orgLevelOneIdAnswer) {
            const answer = orgLevelOneIdAnswer as string;
            selectedOrgIds = [answer];
            this.props.loadOrgStructure(answer);
        }
        if (orgLevelTwoIdAnswer) {
            const answer = orgLevelTwoIdAnswer as string;
            selectedOrgIds = [...selectedOrgIds, answer];
            this.props.loadOrgStructure(answer);
        }
        if (orgLevelThreeIdAnswer) {
            const answer = orgLevelThreeIdAnswer as string;
            selectedOrgIds = [...selectedOrgIds, answer];
            this.props.loadOrgStructure(answer);
        }
        if (orgLevelFourIdAnswer) {
            const answer = orgLevelFourIdAnswer as string;
            selectedOrgIds = [...selectedOrgIds, answer];
            this.props.loadOrgStructure(answer);
        }

        if (selectedOrgIds?.length > 0) {
            this.setState({
                initialOrgStructuresLoaded: true,
                selectedOrgStructureLevelIds: selectedOrgIds
            });
        }
    }

    private updateOrgLevels(oldProps: ReferralFormProps) {
        const updateOrgLevel = (level: number, questionId: string) => {
            const oldOrgLevel = oldProps.questions.find(question => question.id === questionId)?.answer;
            const newOrgLevel = this.getAnswerToQuestionWithId(questionId);
            if (newOrgLevel && oldOrgLevel !== newOrgLevel) {
                this.changeOrgStructureLevel(level, newOrgLevel as string);
            }
        };
        updateOrgLevel(0, questionIds.OrgLevel1Id);
        updateOrgLevel(1, questionIds.OrgLevel2Id);
        updateOrgLevel(2, questionIds.OrgLevel3Id);
        updateOrgLevel(3, questionIds.OrgLevel4Id);
    }

    private checkPhoneNumberIsMandatory = (): boolean => {
        return !this.getAnswerToQuestionWithId(questionIds.WorkPhone) &&
            !this.getAnswerToQuestionWithId(questionIds.HomePhone) &&
            !this.getAnswerToQuestionWithId(questionIds.MobilePhone);
    };

    public render() {
        const questionnaireContent = (<>
            <ContextAwareProgressBarQuestionnaireSection title="Data Privacy (Please tick all that apply)" id="Consent">
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.PrivacyConsent)}
                    readonly={this.props.readonly}
                    value="Granted"
                    hideDivider={this.getDividerStatus([questionIds.DataSecureConsent, questionIds.EmployeeAwareConsent])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.EmployeeAwareConsent)}
                    readonly={this.props.readonly}
                    value="Granted"
                    hideDivider={this.getDividerStatus([questionIds.DataSecureConsent])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.DataSecureConsent)}
                    readonly={this.props.readonly}
                    value="Granted"
                    hideDivider={true}
                />
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection title="Manager/HR Details" id={lineManagerHrSectionId}>
                {isCustomerUser(this.props.currentUser) || this.props.readonly ?
                    <>
                        <TextQuestion
                            questionConfig={({ id: "LineManagerId", questionText: "Line Manager Id" })}
                            hidden
                            readonly
                            answer={this.props.currentUser.id}
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.LineManagerName)}
                            answer={this.props.currentUser.name}
                            readonly
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.LineManagerEmail)}
                            answer={this.props.currentUser.email}
                            readonly
                        />
                        <TextQuestion
                            questionConfig={this.getQuestionConfig(questionIds.LineManagerPhone)}
                            answer={this.props.currentUser.mobilePhone}
                            readonly
                            hideDivider={this.isSelfReferral()}
                        />
                    </> :
                    <>
                        <DropdownQuestion
                            questionConfig={selfReferralQuestion}
                            options={[selfReferralYes, selfReferralNo].map(a => ({ text: a, value: a }))}
                            readonly={this.props.readonly}
                            mandatory
                            hideDivider={this.isSelfReferral()}
                        />
                        {!this.isSelfReferral() &&
                            <>
                                <DropdownQuestion
                                    questionConfig={({ ...this.getQuestionConfig(questionIds.LineManagerName), id: "LineManagerId" })}
                                    options={this.props.lineManagers &&
                                        this.props.lineManagers.map(lineManager => ({ text: lineManager.name, value: lineManager.id }))}
                                    readonly={this.props.readonly}
                                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.SelfReferral)]}
                                    mandatory={this.getAnswerToQuestionWithId(questionIds.SelfReferral) !== selfReferralYes}
                                    onAnswerChange={this.changeLineManager}
                                    hidden={this.props.readonly}
                                />
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.LineManagerName)}
                                    readonly={this.props.readonly}
                                    hidden={!this.props.readonly}
                                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.SelfReferral)]}
                                />
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.LineManagerEmail)}
                                    readonly
                                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.SelfReferral)]}
                                />
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.LineManagerPhone)}
                                    readonly
                                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.SelfReferral)]}
                                />
                            </>
                        }
                    </>
                }
                {!this.isSelfReferral() &&
                    <>
                        {(this.getAnswerToQuestionWithId(questionIds.TotallyConfidential) !== totallyConfidentialAnswer) &&
                            <DropdownQuestion
                                questionConfig={({ ...this.getQuestionConfig(questionIds.HrName), id: "HrId" })}
                                options={this.props.hrRepresentatives &&
                                    this.props.hrRepresentatives.map(hrRepresentative => (
                                        {
                                            text: hrRepresentative.name,
                                            value: hrRepresentative.id
                                        }))}
                                validationDependencies={[this.getAnswerToQuestionWithId(questionIds.SelfReferral)]}
                                mandatory={!this.isSelfReferral()}
                                readonly={this.props.readonly}
                                onAnswerChange={this.changeHrRepresentative}
                                hidden={this.props.readonly}
                            />
                        }
                        {(this.getAnswerToQuestionWithId(questionIds.TotallyConfidential) !== totallyConfidentialAnswer ||
                            (this.props.readonly &&
                                this.getAnswerToQuestionWithId(questionIds.HrName))) &&
                            <>
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.HrName)}
                                    readonly={this.props.readonly}
                                    hidden={!this.props.readonly}
                                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.SelfReferral)]}
                                />
                                <TextQuestion
                                    questionConfig={this.getQuestionConfig(questionIds.HrEmail)}
                                    readonly
                                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.SelfReferral)]}
                                    hideDivider={true}
                                />
                            </>
                        }
                    </>
                }
                <TextQuestion
                    hideDivider
                    questionConfig={this.getQuestionConfig(questionIds.PurchaseOrderNumber)}
                    readonly={this.props.readonly}
                    mandatory={this.getQuestionMandatoryConfig(questionIds.PurchaseOrderNumber, false)}
                    validators={[purchaseOrderNumberValidator]}
                />
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection title="Employee Details" id={employeeDetailSectionId}>
                <TextQuestion
                    hideDivider
                    questionConfig={this.getQuestionConfig(questionIds.ReferredTitle)}
                    readonly={this.props.readonly}
                    validators={[validators.validLength({ max: 25, message: "Maximum of 25 characters only" })]}
                />
                <TextQuestion
                    hideDivider
                    questionConfig={this.getQuestionConfig(questionIds.ReferredFirstName)}
                    readonly={this.props.readonly} mandatory />
                <TextQuestion
                    hideDivider
                    questionConfig={this.getQuestionConfig(questionIds.ReferredLastName)}
                    readonly={this.props.readonly}
                    mandatory
                />
                <DateQuestion
                    hideDivider
                    questionConfig={this.getQuestionConfig(questionIds.ReferredDob)}
                    readonly={this.props.readonly}
                    mandatory={this.getQuestionMandatoryConfig(questionIds.ReferredDob)}
                    validators={dateOfBirthValidators}
                />
                <DropdownQuestion
                    questionConfig={this.getQuestionConfig(questionIds.ReferredGender)}
                    /* eslint-disable-next-line max-len */
                    options={GENDER_OPTIONS}
                    readonly={this.props.readonly}
                    mandatory
                    hideDivider
                />
                <TextQuestion
                    hideDivider
                    questionConfig={this.getQuestionConfig(questionIds.EmployeeId)}
                    readonly={this.props.readonly}
                    mandatory={this.getQuestionMandatoryConfig(questionIds.EmployeeId)}
                    validators={[ validators.validLength({ max: 20 }) ]}
                />
                <TextQuestion hideDivider questionConfig={this.getQuestionConfig(questionIds.JobTitle)} readonly={this.props.readonly} mandatory />
                <TextQuestion hideDivider questionConfig={this.getQuestionConfig(questionIds.AddressLine1)} readonly={this.props.readonly} />
                <TextQuestion hideDivider questionConfig={this.getQuestionConfig(questionIds.AddressLine2)} readonly={this.props.readonly} />
                <TextQuestion hideDivider questionConfig={this.getQuestionConfig(questionIds.AddressLine3)} readonly={this.props.readonly} />
                <TextQuestion hideDivider questionConfig={this.getQuestionConfig(questionIds.City)} readonly={this.props.readonly} />
                <TextQuestion hideDivider questionConfig={this.getQuestionConfig(questionIds.County)} readonly={this.props.readonly} />
                <TextQuestion
                    hideDivider
                    questionConfig={this.getQuestionConfig(questionIds.Postcode)}
                    readonly={this.props.readonly}
                    mandatory={this.getQuestionMandatoryConfig(questionIds.Postcode)}
                />
                <TextQuestion
                    hideDivider
                    questionConfig={this.getQuestionConfig(questionIds.PersonalEmailAddress)}
                    readonly={this.props.readonly}
                    mandatory />
                <TextQuestion
                    hideDivider questionConfig={this.getQuestionConfig(questionIds.ConfirmPersonalEmailAddress)}
                    readonly={this.props.readonly}
                    validators={[createValidator(answer =>
                        answer === this.getAnswerToQuestionWithId(questionIds.PersonalEmailAddress), "Email addresses do not match")]}
                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.PersonalEmailAddress)]}
                    mandatory
                />
                <TextQuestion
                    hideDivider questionConfig={this.getQuestionConfig(questionIds.MobilePhone)}
                    readonly={this.props.readonly}
                    validators={[validators.validPhoneNumber(10)]}
                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.HomePhone), this.getAnswerToQuestionWithId(questionIds.WorkPhone)]}
                    mandatory={this.checkPhoneNumberIsMandatory()}
                    errorMessage="At least one valid telephone number must be provided"
                />
                <TextQuestion
                    hideDivider questionConfig={this.getQuestionConfig(questionIds.HomePhone)}
                    readonly={this.props.readonly}
                    validators={[validators.validPhoneNumber(10)]}
                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.MobilePhone), this.getAnswerToQuestionWithId(questionIds.WorkPhone)]}
                    mandatory={this.checkPhoneNumberIsMandatory()}
                    errorMessage="At least one valid telephone number must be provided"
                />
                <TextQuestion
                    hideDivider questionConfig={this.getQuestionConfig(questionIds.WorkPhone)}
                    readonly={this.props.readonly}
                    validators={[validators.validPhoneNumber(10)]}
                    validationDependencies={[this.getAnswerToQuestionWithId(questionIds.MobilePhone), this.getAnswerToQuestionWithId(questionIds.HomePhone)]}
                    mandatory={this.checkPhoneNumberIsMandatory()}
                    errorMessage="At least one valid telephone number must be provided"
                />
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection title="Employee Details Cont'd" id={furtherDetailsSectionId}>
                <DropdownQuestion
                    questionConfig={({ ...this.getQuestionConfig(questionIds.OrgLevel1Name), id: questionIds.OrgLevel1Id })}
                    options={this.props.orgLevels &&
                        this.props.orgLevels.filter(orgLevel => !orgLevel.parentId)
                            .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id }))}
                    // eslint-disable-next-line react/jsx-no-bind
                    onAnswerChange={orgStructureId => this.changeOrgStructureLevel(0, orgStructureId as string)}
                    search
                    readonly={this.props.readonly}
                    hidden={this.props.readonly}
                    hideDivider
                    mandatory
                />
                <TextQuestion
                    questionConfig={this.getQuestionConfig(questionIds.OrgLevel1Name)}
                    hidden={!this.props.readonly}
                    readonly
                    hideDivider
                />
                <DropdownQuestion
                    questionConfig={({ ...this.getQuestionConfig(questionIds.OrgLevel2Name), id: questionIds.OrgLevel2Id })}
                    options={(this.props.orgLevels && this.state.selectedOrgStructureLevelIds[0]) ?
                        this.props.orgLevels.filter(orgLevel => orgLevel.parentId === this.state.selectedOrgStructureLevelIds[0])
                            .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) : undefined}
                    // eslint-disable-next-line react/jsx-no-bind
                    onAnswerChange={orgStructureId => this.changeOrgStructureLevel(1, orgStructureId as string)}
                    search
                    readonly={this.props.readonly}
                    hidden={this.props.readonly || !this.state.selectedOrgStructureLevelIds[0]}
                    mandatory={this.getQuestionMandatoryConfig(questionIds.OrgLevel2Name, false)}
                    hideDivider
                />
                <TextQuestion
                    questionConfig={this.getQuestionConfig(questionIds.OrgLevel2Name)}
                    hidden={!this.props.readonly}
                    readonly
                    hideDivider
                />
                <DropdownQuestion
                    questionConfig={({ ...this.getQuestionConfig(questionIds.OrgLevel3Name), id: questionIds.OrgLevel3Id })}
                    options={(this.props.orgLevels && this.state.selectedOrgStructureLevelIds[1]) ?
                        this.props.orgLevels.filter(orgLevel => orgLevel.parentId === this.state.selectedOrgStructureLevelIds[1])
                            .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) : undefined}
                    // eslint-disable-next-line react/jsx-no-bind
                    onAnswerChange={orgStructureId => this.changeOrgStructureLevel(2, orgStructureId as string)}
                    search
                    readonly={this.props.readonly}
                    hidden={this.props.readonly || !this.state.selectedOrgStructureLevelIds[1]}
                    mandatory={this.getQuestionMandatoryConfig(questionIds.OrgLevel3Name, false)}
                    hideDivider
                />
                <TextQuestion
                    questionConfig={this.getQuestionConfig(questionIds.OrgLevel3Name)}
                    hidden={!this.props.readonly}
                    readonly
                    hideDivider
                />
                <DropdownQuestion
                    questionConfig={({ ...this.getQuestionConfig(questionIds.OrgLevel4Name), id: questionIds.OrgLevel4Id })}
                    options={(this.props.orgLevels && this.state.selectedOrgStructureLevelIds[2]) ?
                        this.props.orgLevels.filter(orgLevel => orgLevel.parentId === this.state.selectedOrgStructureLevelIds[2])
                            .map(orgLevel => ({ text: orgLevel.name, value: orgLevel.id })) : undefined}
                    // eslint-disable-next-line react/jsx-no-bind
                    onAnswerChange={orgStructureId => this.changeOrgStructureLevel(3, orgStructureId as string)}
                    search
                    readonly={this.props.readonly}
                    hidden={this.props.readonly || !this.state.selectedOrgStructureLevelIds[2]}
                    hideDivider
                />
                <TextQuestion
                    questionConfig={this.getQuestionConfig(questionIds.OrgLevel4Name)}
                    hidden={!this.props.readonly}
                    readonly
                    hideDivider
                />
                <TextQuestion hideDivider questionConfig={this.getQuestionConfig(questionIds.ShiftPattern)} readonly={this.props.readonly} />
                <TextQuestion hideDivider questionConfig={this.getQuestionConfig(questionIds.StandardHours)} readonly={this.props.readonly} />
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection title="Employee Benefits" id="Benefits">
                <RadioButtonQuestion
                    questionConfig={this.getQuestionConfig(questionIds.PrivateMedicalInsurance)}
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([questionIds.Physiotherapy, questionIds.EmployeeAssistedProgramme])}
                >
                    <RadioButton label="Yes" value="Yes" />
                    <RadioButton label="No" value="No" />
                </RadioButtonQuestion>
                <RadioButtonQuestion
                    questionConfig={this.getQuestionConfig(questionIds.EmployeeAssistedProgramme)}
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([questionIds.Physiotherapy])}
                >
                    <RadioButton label="Yes" value="Yes" />
                    <RadioButton label="No" value="No" />
                </RadioButtonQuestion>
                <RadioButtonQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Physiotherapy)}
                    readonly={this.props.readonly}
                    hideDivider={true}
                >
                    <RadioButton label="Yes" value="Yes" />
                    <RadioButton label="No" value="No" />
                </RadioButtonQuestion>
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection title="Absence Details" id="AbsenceDetails">
                <RadioButtonQuestion
                    questionConfig={this.getQuestionConfig(this.absentQuestion.id)}
                    readonly={this.props.readonly}
                    onAnswerChange={this.changeAbsent}
                    hideDivider={this.getDividerStatus([this.restrictedDutiesQuestion.id, questionIds.PreviouslyReferred])
                        || this.getAnswerToQuestionWithId(this.absentQuestion.id) === this.absentQuestion.absentAnswer}
                >
                    <RadioButton label="Yes" value={this.absentQuestion.absentAnswer} />
                    <RadioButton label="No" value="No" />
                </RadioButtonQuestion>
                {this.getAnswerToQuestionWithId(this.absentQuestion.id) === this.absentQuestion.absentAnswer &&
                    <DateQuestion
                        questionConfig={this.getQuestionConfig(questionIds.AbsenceStart)}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider
                    />
                }
                <RadioButtonQuestion
                    questionConfig={this.getQuestionConfig(questionIds.PreviouslyReferred)}
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([this.restrictedDutiesQuestion.id])}
                >
                    <RadioButton label="Yes" value="Yes" />
                    <RadioButton label="No" value="No" />
                </RadioButtonQuestion>
                <RadioButtonQuestion
                    questionConfig={this.getQuestionConfig(this.restrictedDutiesQuestion.id)}
                    readonly={this.props.readonly}
                    hideDivider={true}
                >
                    <RadioButton label="Yes" value="Yes" />
                    <RadioButton label="No" value="No" />
                </RadioButtonQuestion>
                <TextQuestion
                    questionConfig={this.getQuestionConfig(questionIds.RestrictedDutiesDetails)}
                    readonly={this.props.readonly}
                    hideDivider={true}
                    multiline={true}
                    rowCount={5}
                />
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection id="Reasons" title="Referral Reasons">
                <p className="additional-spacing-paragraph">
                    In order for us to ensure that we deal with your referral in the most effective way,
                    please select the reasons for referral (select all that apply)
                </p>
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Recurrent)}
                    value="Recurrent Short Spells of Absence Causing Commercial Harm"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.IllHealthRetirement,
                        questionIds.AlcoholOrDrugMisuse,
                        questionIds.PoorPerformance,
                        questionIds.RequirementsChanged,
                        questionIds.Accident,
                        questionIds.FitnessForDisciplinary,
                        questionIds.FitnessForWork,
                        questionIds.MusculoSkeletal,
                        questionIds.MentalHealth,
                        questionIds.Prolonged
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Prolonged)}
                    value="Prolonged Sickness Absence (give date when absence commenced, and any diagnosis given)"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.IllHealthRetirement,
                        questionIds.AlcoholOrDrugMisuse,
                        questionIds.PoorPerformance,
                        questionIds.RequirementsChanged,
                        questionIds.Accident,
                        questionIds.FitnessForDisciplinary,
                        questionIds.FitnessForWork,
                        questionIds.MusculoSkeletal,
                        questionIds.MentalHealth
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.MentalHealth)}
                    value="Mental Health Condition (stress, anxiety, depression)"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.IllHealthRetirement,
                        questionIds.AlcoholOrDrugMisuse,
                        questionIds.PoorPerformance,
                        questionIds.RequirementsChanged,
                        questionIds.Accident,
                        questionIds.FitnessForDisciplinary,
                        questionIds.FitnessForWork,
                        questionIds.MusculoSkeletal
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.MusculoSkeletal)}
                    value="Musculoskeletal Condition"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.IllHealthRetirement,
                        questionIds.AlcoholOrDrugMisuse,
                        questionIds.PoorPerformance,
                        questionIds.RequirementsChanged,
                        questionIds.Accident,
                        questionIds.FitnessForDisciplinary,
                        questionIds.FitnessForWork
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.FitnessForWork)}
                    value="Fitness for Work"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.IllHealthRetirement,
                        questionIds.AlcoholOrDrugMisuse,
                        questionIds.PoorPerformance,
                        questionIds.RequirementsChanged,
                        questionIds.Accident,
                        questionIds.FitnessForDisciplinary
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.FitnessForDisciplinary)}
                    value="Fitness to Attend Disciplinary Hearing/Meeting"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.IllHealthRetirement,
                        questionIds.AlcoholOrDrugMisuse,
                        questionIds.PoorPerformance,
                        questionIds.RequirementsChanged,
                        questionIds.Accident
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Accident)}
                    value="Assessment of Fitness Following an Accident at Work"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.IllHealthRetirement,
                        questionIds.AlcoholOrDrugMisuse,
                        questionIds.PoorPerformance,
                        questionIds.RequirementsChanged
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.RequirementsChanged)}
                    value="Job Requirements will be Changing/Have Changed"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.IllHealthRetirement,
                        questionIds.AlcoholOrDrugMisuse,
                        questionIds.PoorPerformance
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.PoorPerformance)}
                    value="Poor Performance at Work"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([questionIds.IllHealthRetirement, questionIds.AlcoholOrDrugMisuse])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.AlcoholOrDrugMisuse)}
                    value="Suspect Alcohol or Drug Misuse"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([questionIds.IllHealthRetirement])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.IllHealthRetirement)}
                    value="Ill Health Retirement"
                    readonly={this.props.readonly}
                    hideDivider={true}
                />
                <TextQuestion
                    questionConfig={this.getQuestionConfig(questionIds.AbsenceReason)}
                    multiline
                    readonly={this.props.readonly}
                    hideDivider={true}
                />
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection id="InformationRequired" title="Required Information">
                <p>
                    Please note that all our clinical reports will include as standard, information about the employee’s current health
                    status, their prognosis for any identified health condition, advice on functional ability and advice on any work
                    restrictions or adaptations including whether these are likely to be short term or long term.  If the employee is
                    currently off work, we will also include a likely return to work date and if deemed necessary a specific rehabilitation
                    or return to work plan with clear timeframes.
                </p>
                <p className="additional-spacing-paragraph">
                    If you would like any additional questions answered please indicate them here
                </p>
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.WorkRelated)}
                    value="Is the ill health work related?"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.ReferToSpecialist,
                        questionIds.ImpairedPerformance,
                        questionIds.ReliableInFuture,
                        questionIds.FitForDisciplinary,
                        questionIds.FitToContinue,
                        questionIds.EqualityAct,
                        questionIds.AdjustmentsRequired,
                        questionIds.RestrictionsApply,
                        questionIds.ReturnDate
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.ReturnDate)}
                    value="Likely date of return to work"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.ReferToSpecialist,
                        questionIds.ImpairedPerformance,
                        questionIds.ReliableInFuture,
                        questionIds.FitForDisciplinary,
                        questionIds.FitToContinue,
                        questionIds.EqualityAct,
                        questionIds.AdjustmentsRequired,
                        questionIds.RestrictionsApply
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.RestrictionsApply)}
                    value="Do any temporary or permanent restrictions apply? and if so, for how long?"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.ReferToSpecialist,
                        questionIds.ImpairedPerformance,
                        questionIds.ReliableInFuture,
                        questionIds.FitForDisciplinary,
                        questionIds.FitToContinue,
                        questionIds.EqualityAct,
                        questionIds.AdjustmentsRequired
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.AdjustmentsRequired)}
                    value="Should there be any adjustments to their days/hours of work to help facilitate their return to work?
                and if so, how long would this adjustment to days/hours be required?"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.ReferToSpecialist,
                        questionIds.ImpairedPerformance,
                        questionIds.ReliableInFuture,
                        questionIds.FitForDisciplinary,
                        questionIds.FitToContinue,
                        questionIds.EqualityAct
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.EqualityAct)}
                    value="In your opinion, would this person's condition be covered by the Equality Act 2010? and if so, what adjustments should
                be considered? (Please note that this is purely an opinion and is ultimately a decision for the courts as to who is covered
                by the Equality Act 2010)"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.ReferToSpecialist,
                        questionIds.ImpairedPerformance,
                        questionIds.ReliableInFuture,
                        questionIds.FitForDisciplinary,
                        questionIds.FitToContinue
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.FitToContinue)}
                    value="Is the individual fit to continue in their current post?"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.ReferToSpecialist,
                        questionIds.ImpairedPerformance,
                        questionIds.ReliableInFuture,
                        questionIds.FitForDisciplinary
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.FitForDisciplinary)}
                    value="Is the individual medically fit to attend disciplinary hearing/meeting?"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([
                        questionIds.ReferToSpecialist,
                        questionIds.ImpairedPerformance,
                        questionIds.ReliableInFuture
                    ])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.ReliableInFuture)}
                    value="Is the individual likely to render a reliable service and attendance in the future?"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([questionIds.ReferToSpecialist, questionIds.ImpairedPerformance])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.ImpairedPerformance)}
                    value="Does the individual have a health condition that could lead to impaired performance?
                If so, in what areas and for how long is it likely to continue?"
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([questionIds.ReferToSpecialist])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.ReferToSpecialist)}
                    value="Would a further referral to a specialist (e.g. counselling/physiotherapy) aid the individual's recovery?"
                    readonly={this.props.readonly}
                    hideDivider={true}
                />
                <TextQuestion
                    questionConfig={this.getQuestionConfig(questionIds.AdditionalInfo)}
                    multiline
                    readonly={this.props.readonly}
                    hideDivider={true}
                />
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection id="ManagerAttendance" title="Appointment">
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.FaceToFaceAttend)}
                    value="If a face to face appointments is arranged I would like to attend the last 10 minutes of the appointment
                    and I have consent from the employee to do this."
                    readonly={this.props.readonly}
                    hideDivider={this.getDividerStatus([questionIds.TelephoneDiscussion])}
                />
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.TelephoneDiscussion)}
                    value="If a telephone appointment or video consultation is arranged I would like to the clinician to ring me at
                    the end of the consultation to discuss any adjustments.  I understand I must make myself available for the phone
                    call and that the Clinician will only have 5 minutes available for the call."
                    readonly={this.props.readonly}
                    hideDivider={true}
                />
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection id={this.jobRoleDetailsSectionId} title="Job Role Detail">
                <p className="additional-spacing-paragraph">
                    Please indicate if any of the following are associated with the individual's job role. If ticked, please give further details
                    <em>(e.g. lone working ticked - how frequently, how long for, etc.)</em>
                </p>
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.ProlongedStanding)}
                    value="Prolonged Standing"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.ProlongedStanding)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse,
                            questionIds.Pushing,
                            questionIds.Pulling,
                            questionIds.Twisting,
                            questionIds.HeavyLifting,
                            questionIds.ShiftWork,
                            questionIds.Climbing,
                            questionIds.Walking,
                            questionIds.Driving,
                            questionIds.ProlongedSitting
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.ProlongedStanding) &&
                    <TextQuestion
                        questionConfig={this.prolongedStandingDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.ProlongedSitting)}
                    value="Prolonged Sitting"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.ProlongedSitting)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse,
                            questionIds.Pushing,
                            questionIds.Pulling,
                            questionIds.Twisting,
                            questionIds.HeavyLifting,
                            questionIds.ShiftWork,
                            questionIds.Climbing,
                            questionIds.Walking,
                            questionIds.Driving
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.ProlongedSitting) &&
                    <TextQuestion
                        questionConfig={this.prolongedSittingDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Driving)}
                    value="Driving"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.Driving)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse,
                            questionIds.Pushing,
                            questionIds.Pulling,
                            questionIds.Twisting,
                            questionIds.HeavyLifting,
                            questionIds.ShiftWork,
                            questionIds.Climbing,
                            questionIds.Walking
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.Driving) &&
                    <TextQuestion
                        questionConfig={this.drivingDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Walking)}
                    value="Walking"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.Walking)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse,
                            questionIds.Pushing,
                            questionIds.Pulling,
                            questionIds.Twisting,
                            questionIds.HeavyLifting,
                            questionIds.ShiftWork,
                            questionIds.Climbing
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.Walking) &&
                    <TextQuestion
                        questionConfig={this.walkingDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Climbing)}
                    value="Climbing"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.Climbing)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse,
                            questionIds.Pushing,
                            questionIds.Pulling,
                            questionIds.Twisting,
                            questionIds.HeavyLifting,
                            questionIds.ShiftWork
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.Climbing) &&
                    <TextQuestion
                        questionConfig={this.climbingDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.ShiftWork)}
                    value="Exposure to Night/Shift Work"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.ShiftWork)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse,
                            questionIds.Pushing,
                            questionIds.Pulling,
                            questionIds.Twisting,
                            questionIds.HeavyLifting
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.ShiftWork) &&
                    <TextQuestion
                        questionConfig={this.shiftWorkDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.HeavyLifting)}
                    value="Lifting or Carrying Heavy Items"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.HeavyLifting)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse,
                            questionIds.Pushing,
                            questionIds.Pulling,
                            questionIds.Twisting
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.HeavyLifting) &&
                    <TextQuestion
                        questionConfig={this.heavyLiftingDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Twisting)}
                    value="Twisting"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.Twisting)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse,
                            questionIds.Pushing,
                            questionIds.Pulling
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.Twisting) &&
                    <TextQuestion
                        questionConfig={this.twistingDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Pulling)}
                    value="Pulling"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.Pulling)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse,
                            questionIds.Pushing
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.Pulling) &&
                    <TextQuestion
                        questionConfig={this.pullingDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Pushing)}
                    value="Pushing"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.Pushing)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace,
                            questionIds.Dse
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.Pushing) &&
                    <TextQuestion
                        questionConfig={this.pushingDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Dse)}
                    value="Computer Work/Display Screen Equipment"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.Dse)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals,
                            questionIds.ConfinedSpace
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.Dse) &&
                    <TextQuestion
                        questionConfig={this.dseDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.ConfinedSpace)}
                    value="Working in Confined Space"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.ConfinedSpace)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents,
                            questionIds.Chemicals
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.ConfinedSpace) &&
                    <TextQuestion
                        questionConfig={this.confinedSpaceDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Chemicals)}
                    value="Working with Chemicals"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.Chemicals)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery,
                            questionIds.BiologicalAgents
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.Chemicals) &&
                    <TextQuestion
                        questionConfig={this.chemicalsDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.BiologicalAgents)}
                    value="Working with Biological Agents"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.BiologicalAgents)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes,
                            questionIds.Machinery
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.BiologicalAgents) &&
                    <TextQuestion
                        questionConfig={this.biologicalAgentsDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.Machinery)}
                    value="Working with Machinery"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.Machinery)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards,
                            questionIds.DustOrFumes
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.Machinery) &&
                    <TextQuestion
                        questionConfig={this.machineryDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.DustOrFumes)}
                    value="Working with Dust or Fumes"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.DustOrFumes)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork,
                            questionIds.NoiseHazards
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.DustOrFumes) &&
                    <TextQuestion
                        questionConfig={this.dustOrFumesDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.NoiseHazards)}
                    value="Exposure to Noise Hazards"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.NoiseHazards)
                        || this.getDividerStatus([
                            questionIds.WorkplaceStress,
                            questionIds.OverseasTravel,
                            questionIds.OutsideWork
                        ])}
                />
                {this.getAnswerToQuestionWithId(questionIds.NoiseHazards) &&
                    <TextQuestion
                        questionConfig={this.noiseHazardsDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.OutsideWork)}
                    value="Outside Work"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.OutsideWork)
                        || this.getDividerStatus([questionIds.WorkplaceStress, questionIds.OverseasTravel])}
                />
                {this.getAnswerToQuestionWithId(questionIds.OutsideWork) &&
                    <TextQuestion
                        questionConfig={this.outsideWorkDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.OverseasTravel)}
                    value="Occasional Overseas Travel"
                    readonly={this.props.readonly}
                    hideDivider={!!this.getAnswerToQuestionWithId(questionIds.OverseasTravel)
                        || this.getDividerStatus([questionIds.WorkplaceStress])}
                />
                {this.getAnswerToQuestionWithId(questionIds.OverseasTravel) &&
                    <TextQuestion
                        questionConfig={this.overseasTravelDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.WorkplaceStress)}
                    value="Exposure to Workplace Stress (Deadlines/Targets)"
                    readonly={this.props.readonly}
                    hideDivider={true}
                />
                {this.getAnswerToQuestionWithId(questionIds.WorkplaceStress) &&
                    <TextQuestion
                        questionConfig={this.workplaceStressDetailsQuestion}
                        readonly={this.props.readonly}
                        mandatory
                        hideDivider={true}
                    />
                }
                <TextQuestion
                    questionConfig={this.getQuestionConfig(questionIds.OtherRoleFunctions)}
                    readonly={this.props.readonly}
                    hideDivider={true}
                />
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection id={AdditionalQuestionsSectionId} title={FurtherQuestionsSectionTitle}>
                {renderAdditionalQuestions(this.props.questionnaireConfig.additionalQuestions, this.props.readonly, true)}
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection id={this.documentsSectionId} title="Supporting Documents">
                <TextQuestion
                    questionConfig={this.getQuestionConfig(questionIds.SupportingDocumentsHeader)}
                    readonly
                    hideDivider
                />
                {this.props.questions
                    .filter(question => question.answer && (question.answer as FileAnswer).blobId !== undefined)
                    .map(fileQuestion =>
                        (
                            <>
                                <FileQuestion
                                    questionConfig={({ id: fileQuestion.id, questionText: "" })}
                                    api={`${this.props.documentBaseApi}/${this.props.ohCaseId ? `${this.props.ohCaseId}/` : ""}document`}
                                    readonly={this.props.readonly}
                                    key={fileQuestion.id}
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onRemove={() => this.props.removeQuestion({ sectionId: this.documentsSectionId, id: fileQuestion.id })}
                                    acceptExtensions={acceptableExtensions}
                                    hideDivider={true}
                                >
                                    <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN]}>
                                        <DeleteReferralFormDocumentModal
                                            customerId={this.props.customerId!}
                                            ohCaseId={this.props.ohCaseId!}
                                            documentId={ (fileQuestion.answer as FileAnswer).blobId }
                                        />
                                    </Authorize>
                                </FileQuestion>
                            </>)
                    )}
                {!this.props.readonly &&
                    <>
                        <Button basic content="Add" onClick={this.addDocument} />
                        <Divider />
                    </>
                }
            </ContextAwareProgressBarQuestionnaireSection>
            <ContextAwareProgressBarQuestionnaireSection id={confidentialitySectionId} title="Confidentiality">
                {this.getAnswerToQuestionWithId(questionIds.TotallyConfidential) === totallyConfidentialAnswer &&
                    <p className="additional-spacing-paragraph">
                        <Label basic color="red">
                            By ticking this box, you have hidden this case from everyone in your organisation
                            including the HR Team apart from yourself and the employee you are referring.
                            If you do not wish to hide this case, please DO NOT TICK this box and
                            ensure that it remains unticked when you submit your referral.
                        </Label>
                    </p>
                }
                <CheckboxQuestion
                    questionConfig={this.getQuestionConfig(questionIds.TotallyConfidential)}
                    readonly={this.props.readonly || this.isSelfReferral()}
                    value={totallyConfidentialAnswer}
                    hideDivider={true}
                />
            </ContextAwareProgressBarQuestionnaireSection>
        </>);
        return (
            <Container className="referral">
                {this.props.readonly ?
                    <>
                        <Container>
                            <Segment basic>
                                <Header as="h1">Referral</Header>
                            </Segment>
                        </Container>
                        {this.props.referralSubmittedDate && <h4>{this.props.referralSubmittedDate.format(DATE_FORMAT)}</h4>}
                    </>
                    :
                    <Container>
                        <Segment basic>
                            <Grid>
                                <Grid.Row>
                                    <Grid.Column width={14}>
                                        <Header as="h1">Occupational Health Referral Form</Header>
                                    </Grid.Column>
                                    <Grid.Column width={2}>
                                        <ConfirmedButton
                                            onClick={this.clearQuestionnaire}
                                            floated="right"
                                            size="mini"
                                            confirmButtonColor="green"
                                            promptText="Are you sure you want to clear this form?"
                                            confirmText="Yes"
                                            cancelText="No"
                                            className="warning"
                                        >
                                            Clear Form
                                        </ConfirmedButton>
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                        </Segment>
                    </Container>
                }
                <>
                    {this.props.currentUser.id || this.props.readonly ?
                        <ContextAwareProgressBarQuestionnaireContainer
                            loadQuestionnaire={this.loadQuestionnaire}
                            submitQuestionnaire={this.submitQuestionnaire}
                            autoSaveQuestionnaire={this.autoSaveQuestionnaire}
                            readonly={this.props.readonly}
                            questionnaireKey={REFERRAL_QUESTIONNAIRE_KEY}
                            questionnaireType={QuestionnaireTypes.Other}
                        >
                            {questionnaireContent}
                        </ContextAwareProgressBarQuestionnaireContainer> :
                        <Loader active />}
                </>
            </Container>
        );
    }

    private changeLineManager = (lineManagerId: string) => {
        const newLineManager = this.props.lineManagers.find(lineManager => lineManager.id === lineManagerId);
        // Line manager name and email questions cannot be disabled so will always be a CustomQuestionConfig
        const lineManagerNameQuestionConfig = this.getQuestionConfig(questionIds.LineManagerName) as CustomQuestionConfig;
        const lineManagerEmailQuestionConfig = this.getQuestionConfig(questionIds.LineManagerEmail) as CustomQuestionConfig;
        const lineManagerPhoneQuestionConfig = this.getQuestionConfig(questionIds.LineManagerPhone) as CustomQuestionConfig;

        const isValid = !!newLineManager || this.getAnswerToQuestionWithId(questionIds.SelfReferral) === selfReferralYes;

        this.props.updateAnswer({
            sectionId: lineManagerHrSectionId, id: questionIds.LineManagerName,
            question: {
                id: lineManagerNameQuestionConfig.id,
                question: lineManagerNameQuestionConfig.questionText,
                answer: newLineManager?.name,
                isValid
            }
        });
        this.props.updateAnswer({
            sectionId: lineManagerHrSectionId, id: questionIds.LineManagerEmail,
            question: {
                question: lineManagerEmailQuestionConfig.questionText,
                answer: newLineManager?.email,
                isValid
            }
        });
        this.props.updateAnswer({
            sectionId: lineManagerHrSectionId, id: questionIds.LineManagerPhone,
            question: {
                question: lineManagerPhoneQuestionConfig.questionText,
                answer: newLineManager?.telephone,
                isValid
            }
        });
    };

    private changeHrRepresentative = (hrRepresentativeId: string) => {
        const newHrRepresentative = this.props.hrRepresentatives?.find(hrRepresentative => hrRepresentative.id === hrRepresentativeId);
        // Hr name and email questions cannot be disabled so will always be a CustomQuestionConfig
        const hrNameQuestionConfig = this.getQuestionConfig(questionIds.HrName) as CustomQuestionConfig;
        const hrEmailQuestionConfig = this.getQuestionConfig(questionIds.HrEmail) as CustomQuestionConfig;

        const isValid = !!newHrRepresentative || this.getAnswerToQuestionWithId(questionIds.SelfReferral) === selfReferralYes;

        this.props.updateAnswer({
            sectionId: lineManagerHrSectionId, id: questionIds.HrName,
            question: {
                id: hrNameQuestionConfig.id,
                question: hrNameQuestionConfig.questionText,
                answer: newHrRepresentative?.name,
                isValid
            }
        });
        this.props.updateAnswer({
            sectionId: lineManagerHrSectionId, id: questionIds.HrEmail,
            question: {
                question: hrEmailQuestionConfig.questionText,
                answer: newHrRepresentative?.email,
                isValid
            }
        });
    };

    private changeAbsent = (absent: string) => {
        const absenceStartQuestionConfig = this.getQuestionConfig(questionIds.AbsenceStart);
        if (absent !== this.absentQuestion.absentAnswer || isQuestionDisabled(absenceStartQuestionConfig)) {
            this.props.removeQuestion({ sectionId: this.absenceDetailsSectionId, id: questionIds.AbsenceStart });
        } else {
            this.props.updateAnswer({
                sectionId: this.absenceDetailsSectionId, id: questionIds.AbsenceStart,
                question: { answer: "", id: absenceStartQuestionConfig.id, question: absenceStartQuestionConfig.questionText }
            });
        }
    };

    private changeOrgStructureLevel(level: number, selectedId: string) {
        this.setState({ selectedOrgStructureLevelIds: [...this.state.selectedOrgStructureLevelIds.slice(0, level), selectedId] });
        const selectedOrgLevel = this.props.orgLevels?.find(orgLevel => orgLevel.id === selectedId);
        if (!selectedOrgLevel) {
            return;
        }

        const selectedName = selectedOrgLevel.name;
        const isValid = !!selectedName;
        const thisOrgLevelNameQuestion = this.props.questions.find(question => question.id === `${this.orgLevelQuestionId}${level + 1}Name`);
        if (thisOrgLevelNameQuestion) {
            this.props.updateAnswer({
                sectionId: furtherDetailsSectionId,
                id: thisOrgLevelNameQuestion.id,
                question: {
                    ...thisOrgLevelNameQuestion,
                    answer: selectedName,
                    isValid
                },
            });
        }
        for (let i = level + 2; i <= 4; i++) {
            const orgLevelIdQuestion = this.props.questions.find(question => question.id === `${this.orgLevelQuestionId}${i}Id`);

            if (!this.canSubmitFormWhenOrgLevelsAreDisabled || orgLevelIdQuestion) {
                this.props.updateAnswer({
                    sectionId: furtherDetailsSectionId,
                    id: orgLevelIdQuestion!.id,
                    question: { ...orgLevelIdQuestion!, answer: "" }
                });
            }

            const orgLevelNameQuestion = this.props.questions.find(question => question.id === `${this.orgLevelQuestionId}${i}Name`);

            if (!this.canSubmitFormWhenOrgLevelsAreDisabled || orgLevelNameQuestion) {
                this.props.updateAnswer({
                    sectionId: furtherDetailsSectionId,
                    id: orgLevelNameQuestion!.id,
                    question: { ...orgLevelNameQuestion!, answer: "" }
                });
            }
        }

        const submittableReferralFormOwnProps = this.props as SubmittableReferralFormOwnProps;

        if (submittableReferralFormOwnProps && submittableReferralFormOwnProps.loadOrgStructure) {
            submittableReferralFormOwnProps.loadOrgStructure(selectedId);
        }
    }

    private getAnswerToQuestionWithId(id: string) {
        return this.props.questions.find(question => question.id === id)?.answer;
    }

    private addDocument() {
        this.props.updateAnswer({
            sectionId: this.documentsSectionId,
            id: `Document${this.state.documentCount + 1}`,
            question: { question: "", answer: { blobId: "", fileName: "" } }
        });
        this.setState({ documentCount: this.state.documentCount + 1 });
    }

    private getQuestionConfig(questionId: string) {
        return this.props.questionnaireConfig.questions.find(c => c.id === questionId) || { id: questionId, disabled: true };
    }

    private getQuestionMandatoryConfig(questionId: string, defaultValue: boolean = true): boolean {
        return isQuestionMandatory(this.props.questionnaireConfig, questionId, defaultValue);
    }

    private getDividerStatus(questionsBelow: string[]) {
        return !this.props.questionnaireConfig.questions.filter(q => questionsBelow.indexOf(q.id) !== -1).some(q => !q.disabled);
    }

    private anyAnswerTicked(questionsToCheck: string[]) {
        return this.props.questions.filter(q => questionsToCheck.indexOf(q.id) !== -1).some(q => !!q.answer);
    }

    private isSelfReferral() {
        const answer = this.getAnswerToQuestionWithId(questionIds.SelfReferral);
        return answer === selfReferralYes;
    }

    private hasSelfReferralAnswerChanged(oldSelfReferralAnswer: QuestionAnswer) {
        const newAnswer = this.props.questions.find(q => q.id === questionIds.SelfReferral)?.answer;
        if (!newAnswer) {
            return false;
        }

        return oldSelfReferralAnswer !== newAnswer;
    }

    private hasOrgLevel1AnswerChanged(oldOrgLevel1Answer: QuestionAnswer) {
        const newAnswer = this.props.questions.find(q => q.id === questionIds.OrgLevel1Name)?.answer;
        return oldOrgLevel1Answer !== newAnswer;
    }

    private changeReferralTotallyConfidentialAnswer(isSelfReferral: boolean) {
        const totallyConfidentialQuestionConfig = this.getQuestionConfig(questionIds.TotallyConfidential) as CustomQuestionConfig;
        const newAnswer = isSelfReferral ? totallyConfidentialAnswer : undefined;
        this.props.updateAnswer({
            sectionId: confidentialitySectionId,
            id: questionIds.TotallyConfidential,
            question: {
                question: totallyConfidentialQuestionConfig.questionText,
                answer: newAnswer,
                isValid: true
            }
        });
    }
}

const mapStateToProps = (
    state: {
        currentUser: { name: string; email: string; mobilePhone: string; id: string; roles: string[] };
        referralForm: ReferralFormState;
        questionnaires: QuestionnairesState;
        router: Location;
    }): ReferralFormStateProps => {
    const referralQuestionnaire = state.questionnaires[REFERRAL_QUESTIONNAIRE_KEY];
    return {
        currentUser: state.currentUser,
        sections: referralQuestionnaire ? referralQuestionnaire.sections : [],
        questions: referralQuestionnaire ?
            uniq(flatten(referralQuestionnaire.sections.map(section => values(section.questions)))) :
            [],
        eTag: referralQuestionnaire?.eTag,
        referralSubmittedDate: referralQuestionnaire?.referralSubmittedDate,
        ohCaseId: state.router.params!.ohCaseId,
        customerId: state.router.params && state.router.params[CUSTOMER_ID],
        questionnaireId: state.router.params && state.router.params[QUESTIONNAIRE_ID],
        ...state.referralForm
    };
};

const mapDispatchToProps = (dispatch: any): ReferralFormDispatchProps => ({
    updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey: REFERRAL_QUESTIONNAIRE_KEY })),
    removeQuestion: payload => dispatch(removeQuestion({ ...payload, questionnaireKey: REFERRAL_QUESTIONNAIRE_KEY })),
    clearQuestionnaire: () => dispatch(clearQuestionnaire(REFERRAL_QUESTIONNAIRE_KEY)),
    resetActiveSections: () => dispatch(resetActiveSections(REFERRAL_QUESTIONNAIRE_KEY)),
    loadQuestionnaire: (requestModel: LoadReferralRequestModel, abortController?: AbortController) =>
        dispatch(loadReferralQuestionnaire(requestModel, abortController)),
    submitQuestionnaire: (apiUrl: string, questions: AnsweredQuestionWithId[], referAgainOhCaseId: string | undefined) =>
        dispatch(submitReferralQuestionnaire(questions, apiUrl, referAgainOhCaseId)),
    saveReferral: (questions: AnsweredQuestionWithId[], eTag: string | null | undefined, isInitial: boolean, referAgainOhCaseId: string | undefined) =>
        dispatch(saveReferralQuestionnaire(questions, eTag, isInitial, referAgainOhCaseId))
});

const ContextAwareReferralForm = connect(mapStateToProps, mapDispatchToProps)(ContextAwareReferralFormUnconnected);

export const ReferralForm = (props: React.PropsWithoutRef<React.ComponentProps<typeof ContextAwareReferralForm>>) => (
    <ContextAwareReferralForm {...props} />);
